import { DatePipe, FormStyle, getLocaleDayNames, getLocaleFirstDayOfWeek, getLocaleMonthNames, TranslationWidth } from '@angular/common';
import { Inject, Injectable, Optional } from '@angular/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import moment, { type Moment, type MomentInput } from 'moment';

type DateNameStyle = 'long' | 'short' | 'narrow';

/**
 * `DateAdapter` for Angular Material datepickers backed by `moment` values.
 *
 * Keeps `moment` for date math, but sources all locale-aware text — month/day/year names and
 * formatted dates — from Angular's i18n data (via {@link DatePipe}) rather than moment's own
 * locale data, which the app builds do not bundle. It also coerces incoming `Date`/ISO-string
 * values to `Moment` so callers may pass either.
 *
 * Provide it alongside the moment date formats:
 * ```ts
 * { provide: DateAdapter, useClass: RsMomentDateAdapter, deps: [MAT_DATE_LOCALE] },
 * { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class RsMomentDateAdapter extends MomentDateAdapter {
  declare protected locale: string;

  constructor(@Optional() @Inject(MAT_DATE_LOCALE) dateLocale: string) {
    super(dateLocale || 'en');
  }

  /** Coerces an empty locale to `'en'`; the base adapter would otherwise call `moment().locale(undefined)`, which reads as a getter. */
  public override setLocale(locale: string): void {
    super.setLocale(locale || 'en');
  }

  public override today(): Moment {
    return moment().locale(this.angularLocale);
  }

  /** Formats through Angular's {@link DatePipe} so output follows the active Angular locale. */
  public override format(date: Moment, displayFormat: string): string {
    const value: Moment = this.toMoment(date);

    if (!value.isValid()) {
      return '';
    }

    const datePipeFormat = displayFormat === 'l' ? 'shortDate' : 'MMM yyyy';

    return new DatePipe(this.locale || 'en').transform(value.toISOString(), datePipeFormat) ?? '';
  }

  public override clone(date: Moment): Moment {
    const cloned: Moment = this.toMoment(date).clone();

    return this.locale ? cloned.locale(this.locale) : cloned;
  }

  /** Month/weekday/year names come from Angular i18n locale data (see class description), not moment. */
  public override getMonthNames(style: DateNameStyle): string[] {
    return [...getLocaleMonthNames(this.angularLocale, FormStyle.Standalone, this.toTranslationWidth(style))];
  }

  public override getDayOfWeekNames(style: DateNameStyle): string[] {
    return [...getLocaleDayNames(this.angularLocale, FormStyle.Standalone, this.toTranslationWidth(style))];
  }

  /**
   * First day of week comes from Angular i18n locale data (see class description), not moment.
   * The base adapter reads moment's locale data, which the app builds do not bundle, so it would
   * fall back to `en` (Sunday) and start the grid on the wrong day for locales like fr/nl (Monday).
   */
  public override getFirstDayOfWeek(): number {
    return getLocaleFirstDayOfWeek(this.angularLocale);
  }

  public override getDateNames(): string[] {
    return Array.from({ length: 31 }, (_unused, index) => String(index + 1));
  }

  public override getYearName(date: Moment): string {
    return new DatePipe(this.angularLocale).transform(this.toMoment(date).toISOString(), 'yyyy') ?? '';
  }

  public override deserialize(value: unknown): Moment | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const candidate: Moment = this.toMoment(value);

    if (!candidate.isValid()) {
      return null;
    }

    return this.locale ? candidate.locale(this.locale) : candidate;
  }

  private get angularLocale(): string {
    return this.locale || 'en';
  }

  private toTranslationWidth(style: DateNameStyle): TranslationWidth {
    switch (style) {
      case 'long':
        return TranslationWidth.Wide;
      case 'short':
        return TranslationWidth.Abbreviated;
      default:
        return TranslationWidth.Narrow;
    }
  }

  private toMoment(date: unknown): Moment {
    return moment.isMoment(date) ? date : moment(date as MomentInput);
  }

  public override createDate(year: number, month: number, date: number): Moment {
    return moment({ year, month, date }).locale(this.angularLocale);
  }

  public override parse(value: unknown, parseFormat: string | string[]): Moment | null {
    if (value && typeof value === 'string') {
      return moment(value, parseFormat, this.angularLocale);
    }

    return value ? moment(value as MomentInput).locale(this.angularLocale) : null;
  }
}
